import Button from "@/components/Button"
import TextInput from "@/components/forms/TextInput"
import Modal from "@/components/modals/Modal"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/modal"
import { ErrorMessage, Field, Form, Formik } from "formik"
import type { Preset } from "@/hooks/presets"
import * as yup from "yup"
import { Checkbox } from "@nextui-org/checkbox"
import { useEffect, useState } from "react"
import { axios } from "@/contexts/auth"
import { ReactComponent as CheckIcon } from "@/icons/check-icon.svg"
import HeadphoneAutocomplete from "@/components/forms/HeadphoneAutocomplete"
import HeadphoneBrandAutocomplete from "@/components/forms/HeadphoneBrandAutocomplete"

type Props = {
  preset: Preset | null
  onConfirm: (values: Preset) => void
  isOpen: boolean
  onClose: () => void
}

type HeadphoneBrand = {
  id: number
  name: string
}

type Headphone = {
  id: number
  brand: HeadphoneBrand
  model: string
}

const RenamePresetModal = ({ preset, onConfirm, isOpen, onClose, ...props }: Props) => {
  const [brands, setBrands] = useState<HeadphoneBrand[]>([])
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(
    preset?.headphone?.brand?.id || null
  )
  const [headphoneModel, setHeadphoneModel] = useState<string>(preset?.headphone?.model || "")
  const [selectedHeadphone, setSelectedHeadphone] = useState<Headphone | null>(
    preset?.headphone || null
  )

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get("/api/equalizer/headphone-brands/")
        setBrands(response.data)
      } catch (error) {
        console.error("Error fetching headphone brands:", error)
      }
    }
    fetchBrands()
  }, [])

  // Update selected brand when preset changes
  useEffect(() => {
    if (preset?.headphone?.brand?.id) {
      setSelectedBrandId(preset.headphone.brand.id)
      setHeadphoneModel(preset.headphone.model)
      setSelectedHeadphone(preset.headphone)
    } else {
      setSelectedBrandId(null)
      setHeadphoneModel("")
      setSelectedHeadphone(null)
    }
  }, [preset])

  if (!preset) {
    return null
  }

  const handleCreateNewHeadphone = async (model: string) => {
    if (!selectedBrandId) throw new Error("No brand selected")

    const response = await axios.post("/api/equalizer/headphones/create/", {
      brand_id: selectedBrandId,
      model: model,
    })
    return response.data
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} {...props}>
      <ModalContent className="overflow-visible">
        {(onClose) => (
          <div className="relative">
            <Formik
              initialValues={{
                name: preset.name || "",
                is_shared: preset.is_shared || false,
                headphone_id: preset.headphone?.id || null,
              }}
              validationSchema={yup.object({
                name: yup.string().min(3).max(100).required("Name is required"),
                is_shared: yup.boolean(),
                headphone_id: yup
                  .number()
                  .nullable()
                  .test(
                    "headphone-required",
                    "You have selected a Headphone Brand. Please select a Headphone Model.",
                    function (value) {
                      // If no brand is selected, any model selection (or none) is valid
                      if (!selectedBrandId) return true
                      // If brand is selected, model must also be selected
                      return value !== null
                    }
                  ),
              })}
              onSubmit={(values) => {
                // Create a new preset object with the updated values
                const updatedPreset: Preset = {
                  ...preset,
                  name: values.name,
                  is_shared: values.is_shared,
                  headphone_id: values.headphone_id,
                  // If headphone_id is null, clear the headphone object
                  headphone: values.headphone_id ? selectedHeadphone : null,
                }
                onConfirm(updatedPreset)
              }}
            >
              {({ errors, touched, values, setFieldValue }) => (
                <Form>
                  <ModalBody>
                    <h2 className="type-body-xl font-bold text-white">Edit your preset</h2>
                    <div className="relative mt-4">
                      <Field
                        as={TextInput}
                        theme="modal"
                        name="name"
                        type="text"
                        autoFocus={true}
                        placeholder="Type preset name"
                        isInvalid={errors.name && touched.name}
                      />
                      <ErrorMessage
                        component="div"
                        className="type-body-xs absolute -top-2 right-0 -translate-y-full text-right italic text-red"
                        name="name"
                      />
                    </div>
                    <div className="mt-4">
                      <Checkbox
                        isSelected={values.is_shared}
                        onValueChange={(value) => setFieldValue("is_shared", value)}
                        classNames={{
                          wrapper: [
                            "bg-cod-gray border border-earth rounded-plus before:border-0 after:bg-transparent text-red mr-2 w-4.5 h-4.5",
                            "group-data-[selected=true]:bg-eclipse group-data-[selected=true]:border-darker-red",
                          ],
                          label: "font-bulo text-[13px] text-ghost",
                          icon: "transition-opacity duration-150 w-3",
                        }}
                        icon={<CheckIcon />}
                        disableAnimation={true}
                      >
                        Share this preset with the community
                      </Checkbox>
                    </div>
                    <div className="mt-6">
                      <h3 className="type-heading-xs flex items-center gap-x-3 font-bold text-earth">
                        <span className="inline-block pt-0.75">Tag a Headphone (optional)</span>
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-x-4">
                        <div>
                          <div className="type-body-xs mb-2 text-silver">Brand</div>
                          <HeadphoneBrandAutocomplete
                            brands={brands}
                            value={selectedBrandId}
                            onChange={(brandId) => {
                              setSelectedBrandId(brandId)
                              // Clear headphone selection when brand changes
                              setFieldValue("headphone_id", null)
                              setHeadphoneModel("")
                              setSelectedHeadphone(null)
                            }}
                          />
                        </div>
                        <div>
                          <div className="type-body-xs mb-2 text-silver">Model</div>
                          <HeadphoneAutocomplete
                            selectedBrandId={selectedBrandId}
                            value={headphoneModel}
                            onChange={(value) => {
                              setHeadphoneModel(value)
                              // If input is cleared, ensure headphone_id is cleared and validation is triggered
                              if (!value.trim()) {
                                setSelectedHeadphone(null)
                                setFieldValue("headphone_id", null, true)
                              }
                            }}
                            onSelect={(headphone) => {
                              setSelectedHeadphone(headphone)
                              setFieldValue("headphone_id", headphone?.id || null, true)
                            }}
                            onCreateNew={handleCreateNewHeadphone}
                            isInvalid={errors.headphone_id && touched.headphone_id}
                            disabled={!selectedBrandId}
                          />
                          <ErrorMessage
                            component="div"
                            className="type-body-xs mt-1 text-right italic text-red"
                            name="headphone_id"
                          />
                        </div>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter className="gap-x-0">
                    <Button theme="blue" size="sm" type="submit">
                      Save Preset
                    </Button>
                    <Button className="-mr-5" theme="none" size="sm" onPress={onClose}>
                      Cancel
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}

export default RenamePresetModal
